import { useEffect, useRef, useState } from 'react';

const benefits = [
  {
    title: 'Fresh Ingredients',
    description: 'Sourced daily from local farms and trusted suppliers',
    image: 'https://readdy.ai/api/search-image?query=close%20up%20of%20fresh%20colorful%20organic%20vegetables%20and%20herbs%20just%20harvested%20from%20farm%2C%20dewy%20morning%20light%2C%20vibrant%20green%20lettuce%20carrots%20tomatoes%20herbs%20arranged%20beautifully%20on%20rustic%20wooden%20surface%2C%20clean%20natural%20food%20photography&width=600&height=380&seq=benefit-fresh-001&orientation=landscape',
  },
  {
    title: 'No Preservatives',
    description: 'Pure, natural ingredients with no artificial additives',
    image: 'https://readdy.ai/api/search-image?query=clean%20minimal%20arrangement%20of%20pure%20natural%20whole%20food%20ingredients%20on%20white%20marble%20surface%2C%20no%20packaging%20no%20labels%2C%20raw%20nuts%20seeds%20grains%20colorful%20spices%20herbs%2C%20overhead%20flat%20lay%20natural%20light%2C%20organic%20food%20photography&width=600&height=380&seq=benefit-nopreserv-001&orientation=landscape',
  },
  {
    title: 'Regional Suppliers',
    description: 'Supporting local farmers and sustainable practices',
    image: 'https://readdy.ai/api/search-image?query=smiling%20local%20farmer%20holding%20basket%20of%20freshly%20picked%20vegetables%20in%20green%20field%2C%20golden%20hour%20light%2C%20countryside%20landscape%20in%20background%2C%20authentic%20regional%20agriculture%2C%20warm%20earthy%20tones%2C%20lifestyle%20photography&width=600&height=380&seq=benefit-regional-001&orientation=landscape',
  },
  {
    title: 'Nutritionist Approved',
    description: 'Every meal is balanced and nutritionally optimized',
    image: 'https://readdy.ai/api/search-image?query=professional%20nutritionist%20or%20dietitian%20reviewing%20healthy%20balanced%20meal%20on%20plate%20with%20nutrition%20chart%2C%20bright%20clinical%20yet%20warm%20office%20setting%2C%20colorful%20food%20with%20protein%20vegetables%20grains%2C%20professional%20healthcare%20photography&width=600&height=380&seq=benefit-nutri-001&orientation=landscape',
  },
  {
    title: 'Fast Delivery',
    description: 'Temperature-controlled delivery within 24 hours',
    image: 'https://readdy.ai/api/search-image?query=modern%20insulated%20food%20delivery%20boxes%20stacked%20neatly%20with%20cold%20pack%20inside%2C%20clean%20white%20packaging%20with%20frost%20condensation%2C%20temperature%20controlled%20meal%20delivery%2C%20product%20photography%20on%20light%20background%2C%20professional%20food%20logistics&width=600&height=380&seq=benefit-delivery-001&orientation=landscape',
  },
  {
    title: 'Eco-Friendly',
    description: 'Sustainable packaging and carbon-neutral delivery',
    image: 'https://readdy.ai/api/search-image?query=eco%20friendly%20sustainable%20food%20packaging%20made%20from%20recycled%20kraft%20paper%20and%20biodegradable%20materials%2C%20green%20leaves%20and%20plants%20around%20boxes%2C%20earthy%20natural%20tones%2C%20sustainability%20concept%20photography%2C%20clean%20white%20background&width=600&height=380&seq=benefit-eco-001&orientation=landscape',
  },
];

export default function Benefits() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-primary-500 text-sm font-semibold tracking-wider uppercase">
            Why Choose Us
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Quality You Can Trust
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We're committed to delivering the highest quality meals with transparency and care.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              {/* Image */}
              <div className="w-full h-44 overflow-hidden">
                <img
                  src={benefit.image}
                  alt={benefit.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}